import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import { formatKabulDisplay } from "./ScheduleFields";
import type { StatusTone } from "../lib/overview-ops";
import {
  driftDisplay,
  drillDisplay,
  phaseDisplay,
  relatedFleetLinks,
  ringDisplay,
  truncateSha256,
  verificationDisplay,
  type DeploySortKey,
  type DriftSortKey,
  type FleetDeployRow,
  type FleetDriftListRow,
  type FleetSiteRow,
  type SiteSortKey,
} from "../lib/fleet-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

function HashButton({ value }: { value: string }) {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm ltr-isolate"
      dir="ltr"
      title={value}
      aria-label={`کپی SHA-256: ${value}`}
      onClick={() => void copyText(value)}
    >
      <bdi dir="ltr" className="ltr-isolate latin-digits">
        {truncateSha256(value)}
      </bdi>
    </button>
  );
}

function FullHash({ value }: { value: string }) {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm ltr-isolate"
      dir="ltr"
      aria-label={`کپی SHA-256 کامل: ${value}`}
      onClick={() => void copyText(value)}
    >
      <bdi dir="ltr" className="ltr-isolate latin-digits">
        {value}
      </bdi>
    </button>
  );
}

/* ─── Site / inventory filters ─── */

type SiteFilterProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: SiteSortKey;
  onSortChange: (value: SiteSortKey) => void;
};

export function FleetSiteFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: SiteFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "pilot", label: "Pilot" },
    { id: "canary", label: "Canary" },
    { id: "production", label: "تولید" },
    { id: "drift", label: "با انحراف" },
    { id: "missing", label: "بدون موجودی" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر موجودی ناوگان"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر موجودی ناوگان"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی سایت ناوگان</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="نام، SITE_CODE، میزبان، مدل…"
          aria-label="جستجوی موجودی ناوگان"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SiteSortKey)}
          aria-label="مرتب‌سازی موجودی ناوگان"
        >
          <option value="name">نام</option>
          <option value="code">SITE_CODE</option>
          <option value="ring">حلقه</option>
          <option value="drift">انحراف</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

/* ─── Deploy filters ─── */

type DeployFilterProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: DeploySortKey;
  onSortChange: (value: DeploySortKey) => void;
};

export function FleetDeployFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: DeployFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "open", label: "باز (قصد/نصب)" },
    { id: "intended", label: "قصد ثبت‌شده" },
    { id: "installed_recorded", label: "نصب ثبت‌شده" },
    { id: "verified", label: "تأییدشده" },
    { id: "cancelled", label: "لغوشده" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر استقرار"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر استقرار"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی استقرار</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="SITE_CODE، نسخه، SHA، فاز…"
          aria-label="جستجوی قصد استقرار"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as DeploySortKey)}
          aria-label="مرتب‌سازی استقرار"
        >
          <option value="intended">زمان قصد</option>
          <option value="site">سایت</option>
          <option value="phase">فاز</option>
          <option value="release">نسخه</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

/* ─── Drift filters ─── */

type DriftFilterProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: DriftSortKey;
  onSortChange: (value: DriftSortKey) => void;
  platformDrift: boolean;
  canPlatformDrift: boolean;
  onPlatformDriftChange: (value: boolean) => void;
};

export function FleetDriftFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  platformDrift,
  canPlatformDrift,
  onPlatformDriftChange,
}: DriftFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "active", label: "فعال" },
    { id: "verified_mismatch", label: "عدم تطبیق" },
    { id: "release_mismatch", label: "نسخه ناهمخوان" },
    { id: "unverified_stale", label: "تأییدنشده کهنه" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر انحراف ناوگان"
    >
      {canPlatformDrift && (
        <div
          className="filter-toolbar__chips announcements-filter-bar__chips"
          role="group"
          aria-label="دامنه انحراف"
        >
          <button
            type="button"
            className={`btn btn--sm ${!platformDrift ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onPlatformDriftChange(false)}
            aria-pressed={!platformDrift}
          >
            سازمان فعلی
          </button>
          <button
            type="button"
            className={`btn btn--sm ${platformDrift ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onPlatformDriftChange(true)}
            aria-pressed={platformDrift}
          >
            همه سازمان‌ها (مالک/اپراتور)
          </button>
        </div>
      )}
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر نوع انحراف"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی انحراف</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="SITE_CODE، نسخه، دلیل…"
          aria-label="جستجوی انحراف ناوگان"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as DriftSortKey)}
          aria-label="مرتب‌سازی انحراف"
        >
          <option value="site">سایت</option>
          <option value="kind">نوع</option>
          <option value="ring">حلقه</option>
          <option value="intended">زمان قصد</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

function RelatedLinks() {
  const links = relatedFleetLinks();
  return (
    <nav className="content-inspect__links" aria-label="مسیرهای مرتبط ناوگان">
      {links.map((link) => (
        <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
          {link.label}
        </Link>
      ))}
    </nav>
  );
}

/* ─── Site inspect ─── */

type SiteInspectProps = {
  item: FleetSiteRow;
  actions?: ReactNode;
};

export function FleetSiteInspectSummary({ item, actions }: SiteInspectProps) {
  const ring = ringDisplay(item.ring);
  const drift = item.driftKind
    ? driftDisplay(item.driftKind, item.driftLabel)
    : null;

  return (
    <section className="content-inspect" aria-labelledby="fleet-site-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">ناوگان · موجودی</p>
          <h2 id="fleet-site-inspect-title" className="content-inspect__name">
            {item.siteName}
          </h2>
          <p className="content-inspect__id">
            SITE_CODE{" "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.siteCode}
            </bdi>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="حلقه و انحراف">
          <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
          {drift && (
            <span className={`state-badge ${TONE_BADGE[drift.tone]}`}>{drift.label}</span>
          )}
          {!item.hasInventory && (
            <span className="state-badge state-badge--archived">بدون موجودی ذخیره‌شده</span>
          )}
        </div>
      </header>

      {ring.detail && <p className="content-inspect__eligibility-detail">{ring.detail}</p>}
      {drift?.detail && <p className="content-inspect__eligibility-detail">{drift.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>مدل روتر</dt>
          <dd>{item.routerModel || "—"}</dd>
        </div>
        <div>
          <dt>نسخه RouterOS</dt>
          <dd>
            {item.routerosVersion ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.routerosVersion}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>میزبان مدیریت</dt>
          <dd>
            {item.managementHost ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.managementHost}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>برچسب دارایی</dt>
          <dd>
            {item.serialAssetTag ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.serialAssetTag}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>پروفایل هات‌اسپات</dt>
          <dd>
            {item.hotspotProfileName ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.hotspotProfileName}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>مسیر پورتال</dt>
          <dd>
            {item.portalDirectoryPath ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.portalDirectoryPath}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>آخرین به‌روزرسانی موجودی</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.inventoryUpdatedAt)}
            </bdi>
          </dd>
        </div>
        {item.driftReason && (
          <div>
            <dt>دلیل انحراف</dt>
            <dd>{item.driftReason}</dd>
          </div>
        )}
        {item.notes && (
          <div>
            <dt>یادداشت</dt>
            <dd>{item.notes}</dd>
          </div>
        )}
      </dl>

      <RelatedLinks />
      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

/* ─── Deploy inspect ─── */

type DeployInspectProps = {
  item: FleetDeployRow;
  actions?: ReactNode;
};

export function FleetDeployInspectSummary({ item, actions }: DeployInspectProps) {
  const phase = phaseDisplay(item.phase);
  const ring = ringDisplay(item.ring);
  const verification = verificationDisplay(item.verificationStatus);
  const drill = drillDisplay(item.rollbackDrillStatus);

  return (
    <section className="content-inspect" aria-labelledby="fleet-deploy-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">ناوگان · قصد استقرار</p>
          <h2 id="fleet-deploy-inspect-title" className="content-inspect__name">
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.expectedTemplateReleaseId}
            </bdi>
          </h2>
          <p className="content-inspect__id">
            سایت{" "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.siteCode}
            </bdi>
            {" · "}
            {item.siteName}
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="فاز و تأیید">
          <span className={`state-badge ${TONE_BADGE[phase.tone]}`}>{phase.label}</span>
          <span className={`state-badge ${TONE_BADGE[verification.tone]}`}>
            {verification.label}
          </span>
          <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
          <span className={`state-badge ${TONE_BADGE[drill.tone]}`}>{drill.label}</span>
        </div>
      </header>

      {phase.detail && <p className="content-inspect__eligibility-detail">{phase.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>SHA الگوی مورد انتظار (کامل)</dt>
          <dd>
            <FullHash value={item.expectedTemplateSha256} />
          </dd>
        </div>
        <div>
          <dt>SHA بسته</dt>
          <dd>{item.artifactSha256 ? <FullHash value={item.artifactSha256} /> : "—"}</dd>
        </div>
        {item.artifactFilename && (
          <div>
            <dt>نام فایل</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.artifactFilename}
              </bdi>
            </dd>
          </div>
        )}
        <div>
          <dt>زمان قصد</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.intendedAt)}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>نسخه نصب ثبت‌شده</dt>
          <dd>
            {item.installedReleaseId ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.installedReleaseId}
              </bdi>
            ) : (
              "— (هنوز اظهار نشده)"
            )}
          </dd>
        </div>
        <div>
          <dt>SHA نصب ثبت‌شده</dt>
          <dd>
            {item.installedSha256 ? <FullHash value={item.installedSha256} /> : "—"}
          </dd>
        </div>
        <div>
          <dt>زمان تأیید</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.verifiedAt)}
            </bdi>
          </dd>
        </div>
        {item.approvedRollbackArtifactId && (
          <div>
            <dt>artifact بازگردانی تأییدشده</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.approvedRollbackArtifactId}
              </bdi>
            </dd>
          </div>
        )}
        {item.driftReason && (
          <div>
            <dt>دلیل انحراف</dt>
            <dd>{item.driftReason}</dd>
          </div>
        )}
        {item.evidenceNotes && (
          <div>
            <dt>یادداشت شواهد</dt>
            <dd>{item.evidenceNotes}</dd>
          </div>
        )}
      </dl>

      <RelatedLinks />
      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

/* ─── Drift inspect ─── */

type DriftInspectProps = {
  item: FleetDriftListRow;
  actions?: ReactNode;
};

export function FleetDriftInspectSummary({ item, actions }: DriftInspectProps) {
  const drift = driftDisplay(item.driftKind, item.driftLabel);
  const ring = ringDisplay(item.ring);
  const phase = item.deploymentPhase ? phaseDisplay(item.deploymentPhase) : null;

  return (
    <section className="content-inspect" aria-labelledby="fleet-drift-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">ناوگان · انحراف</p>
          <h2 id="fleet-drift-inspect-title" className="content-inspect__name">
            {item.siteName}
          </h2>
          <p className="content-inspect__id">
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.siteCode}
            </bdi>
            {item.organizationCode && (
              <>
                {" · org "}
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  {item.organizationCode}
                </bdi>
              </>
            )}
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="وضعیت انحراف">
          <span className={`state-badge ${TONE_BADGE[drift.tone]}`}>{drift.label}</span>
          <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
          {phase && (
            <span className={`state-badge ${TONE_BADGE[phase.tone]}`}>{phase.label}</span>
          )}
        </div>
      </header>

      {drift.detail && <p className="content-inspect__eligibility-detail">{drift.detail}</p>}
      <p className="content-inspect__eligibility-detail">{item.driftLabel}</p>

      <dl className="content-inspect__facts">
        <div>
          <dt>نسخه مورد انتظار</dt>
          <dd>
            {item.expectedReleaseId ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.expectedReleaseId}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>SHA مورد انتظار</dt>
          <dd>
            {item.expectedTemplateSha256 ? (
              <FullHash value={item.expectedTemplateSha256} />
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>نسخه نصب ثبت‌شده</dt>
          <dd>
            {item.recordedInstalledReleaseId ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.recordedInstalledReleaseId}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>وضعیت تأیید</dt>
          <dd>
            {item.verificationStatus
              ? verificationDisplay(item.verificationStatus).label
              : "—"}
          </dd>
        </div>
        <div>
          <dt>زمان قصد</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.intendedAt)}
            </bdi>
          </dd>
        </div>
        {item.deploymentId && (
          <div>
            <dt>شناسه قصد</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.deploymentId}
              </bdi>
            </dd>
          </div>
        )}
        {item.driftReason && (
          <div>
            <dt>دلیل</dt>
            <dd>{item.driftReason}</dd>
          </div>
        )}
      </dl>

      <RelatedLinks />
      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

/* ─── Site list ─── */

type SiteListProps = {
  rows: FleetSiteRow[];
  selectedId: string | null;
  onSelect: (siteId: string) => void;
};

export function FleetSiteInventoryList({ rows, selectedId, onSelect }: SiteListProps) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول موجودی ناوگان"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">سایت</th>
              <th scope="col">SITE_CODE</th>
              <th scope="col">حلقه</th>
              <th scope="col">انحراف</th>
              <th scope="col">مدل / میزبان</th>
              <th scope="col">آخرین به‌روزرسانی</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.siteId === selectedId;
              const ring = ringDisplay(row.ring);
              const drift = row.driftKind
                ? driftDisplay(row.driftKind, row.driftLabel)
                : null;
              return (
                <tr
                  key={row.siteId}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.siteId)}
                      aria-pressed={selected}
                    >
                      {row.siteName}
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.siteCode}>
                      <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
                  </td>
                  <td>
                    {drift ? (
                      <span className={`state-badge ${TONE_BADGE[drift.tone]}`}>
                        {drift.label}
                      </span>
                    ) : (
                      <span className="sites-inventory__muted">—</span>
                    )}
                  </td>
                  <td>
                    {row.routerModel || "—"}
                    {row.managementHost && (
                      <div
                        className="sites-inventory__muted inventory-cell-truncate"
                        title={row.managementHost}
                      >
                        <LtrIsolate mono>{row.managementHost}</LtrIsolate>
                      </div>
                    )}
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.inventoryUpdatedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.inventoryUpdatedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.siteId)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های موجودی ناوگان"
      >
        {rows.map((row) => {
          const selected = row.siteId === selectedId;
          const ring = ringDisplay(row.ring);
          const drift = row.driftKind
            ? driftDisplay(row.driftKind, row.driftLabel)
            : null;
          return (
            <li
              key={row.siteId}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.siteId)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">{row.siteName}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
                  {drift && (
                    <span className={`state-badge ${TONE_BADGE[drift.tone]}`}>{drift.label}</span>
                  )}
                </span>
                <span className="sites-inventory__muted">
                  SITE_CODE:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.siteCode}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  {row.routerModel || "بدون مدل"}
                  {row.managementHost ? (
                    <>
                      {" · "}
                      <bdi dir="ltr" className="ltr-isolate latin-digits">
                        {row.managementHost}
                      </bdi>
                    </>
                  ) : null}
                </span>
                <span className="sites-inventory__muted">
                  به‌روزرسانی:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.inventoryUpdatedAt)}
                  </bdi>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/* ─── Deploy list ─── */

type DeployListProps = {
  rows: FleetDeployRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: FleetDeployRow) => ReactNode;
};

export function FleetDeployInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: DeployListProps) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول قصد استقرار"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">سایت</th>
              <th scope="col">نسخه مورد انتظار</th>
              <th scope="col">SHA</th>
              <th scope="col">فاز</th>
              <th scope="col">تأیید</th>
              <th scope="col">حلقه</th>
              <th scope="col">زمان قصد</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const phase = phaseDisplay(row.phase);
              const verification = verificationDisplay(row.verificationStatus);
              const ring = ringDisplay(row.ring);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                    </button>
                    <div className="sites-inventory__muted">{row.siteName}</div>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={row.expectedTemplateReleaseId}
                    >
                      <LtrIsolate mono>{row.expectedTemplateReleaseId}</LtrIsolate>
                    </div>
                    {row.installedReleaseId && (
                      <div
                        className="sites-inventory__muted inventory-cell-truncate"
                        title={row.installedReleaseId}
                      >
                        نصب:{" "}
                        <LtrIsolate mono>{row.installedReleaseId}</LtrIsolate>
                      </div>
                    )}
                  </td>
                  <td>
                    {row.artifactSha256 ? (
                      <HashButton value={row.artifactSha256} />
                    ) : (
                      <HashButton value={row.expectedTemplateSha256} />
                    )}
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[phase.tone]}`}>{phase.label}</span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[verification.tone]}`}>
                      {verification.label}
                    </span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.intendedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.intendedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های قصد استقرار"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const phase = phaseDisplay(row.phase);
          const verification = verificationDisplay(row.verificationStatus);
          const ring = ringDisplay(row.ring);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.siteCode}
                  </bdi>
                  {" · "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.expectedTemplateReleaseId}
                  </bdi>
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[phase.tone]}`}>{phase.label}</span>
                  <span className={`state-badge ${TONE_BADGE[verification.tone]}`}>
                    {verification.label}
                  </span>
                  <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
                </span>
                <span className="sites-inventory__muted">
                  SHA:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {truncateSha256(row.artifactSha256 ?? row.expectedTemplateSha256)}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  قصد:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.intendedAt)}
                  </bdi>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/* ─── Drift list ─── */

type DriftListProps = {
  rows: FleetDriftListRow[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  renderActions: (row: FleetDriftListRow) => ReactNode;
  showOrg: boolean;
};

export function FleetDriftInventoryList({
  rows,
  selectedKey,
  onSelect,
  renderActions,
  showOrg,
}: DriftListProps) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول انحراف ناوگان"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">سایت</th>
              {showOrg && <th scope="col">سازمان</th>}
              <th scope="col">انحراف</th>
              <th scope="col">حلقه</th>
              <th scope="col">نسخه انتظار / نصب</th>
              <th scope="col">زمان قصد</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.key === selectedKey;
              const drift = driftDisplay(row.driftKind, row.driftLabel);
              const ring = ringDisplay(row.ring);
              return (
                <tr
                  key={row.key}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.key)}
                      aria-pressed={selected}
                    >
                      {row.siteName}
                    </button>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.siteCode}
                    >
                      <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                    </div>
                  </td>
                  {showOrg && (
                    <td>
                      {row.organizationCode ? (
                        <div className="inventory-cell-truncate" title={row.organizationCode}>
                          <LtrIsolate mono>{row.organizationCode}</LtrIsolate>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  )}
                  <td>
                    <span className={`state-badge ${TONE_BADGE[drift.tone]}`}>{drift.label}</span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={row.expectedReleaseId ?? undefined}
                    >
                      <LtrIsolate mono>{row.expectedReleaseId ?? "—"}</LtrIsolate>
                    </div>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.recordedInstalledReleaseId ?? undefined}
                    >
                      نصب:{" "}
                      <LtrIsolate mono>{row.recordedInstalledReleaseId ?? "—"}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.intendedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.intendedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.key)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های انحراف ناوگان"
      >
        {rows.map((row) => {
          const selected = row.key === selectedKey;
          const drift = driftDisplay(row.driftKind, row.driftLabel);
          const ring = ringDisplay(row.ring);
          return (
            <li
              key={row.key}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.key)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">{row.siteName}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[drift.tone]}`}>{drift.label}</span>
                  <span className={`state-badge ${TONE_BADGE[ring.tone]}`}>{ring.label}</span>
                </span>
                <span className="sites-inventory__muted">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.siteCode}
                  </bdi>
                  {showOrg && row.organizationCode ? (
                    <>
                      {" · "}
                      <bdi dir="ltr" className="ltr-isolate latin-digits">
                        {row.organizationCode}
                      </bdi>
                    </>
                  ) : null}
                </span>
                <span className="sites-inventory__muted">
                  انتظار:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.expectedReleaseId ?? "—"}
                  </bdi>
                  {" · نصب: "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.recordedInstalledReleaseId ?? "—"}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  قصد:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.intendedAt)}
                  </bdi>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
